<template>
  <!-- Nothing rendered at all when an appointment has no markers, so the row collapses
       and the customer name keeps its full width. -->
  <template v-if="markers.length">
    <!-- Dense calendar blocks: bare icons. A block can be 13px tall, so a chip would
         either clip or push the name out. -->
    <template v-if="variant === 'icon'">
      <i
        v-for="marker in markers"
        :key="marker.key"
        class="pi flex-none"
        :class="marker.icon"
        :style="{ fontSize: size, color: marker.fg }"
        :title="marker.title"
      />
    </template>

    <!-- Roomy rows: the status chip from the design system — tinted pill, 600 weight. -->
    <span
      v-for="marker in markers"
      v-else
      :key="marker.key"
      class="flex-none inline-flex items-center gap-1 rounded-full font-semibold whitespace-nowrap"
      :style="{ background: marker.bg, color: marker.fg, fontSize: '10px', padding: '2px 8px' }"
      :title="marker.title"
    >
      <i class="pi" :class="marker.icon" style="font-size: 0.6rem" />
      {{ marker.label }}
    </span>
  </template>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { PaymentRequestStatus, type ICalendarEvent } from '@torim-app/types';

const props = withDefaults(defineProps<{
  event: ICalendarEvent;
  /** Icon size for the `icon` variant — the calendar shrinks it on tight blocks. */
  size?: string;
  /** `icon` for dense calendar grids, `pill` where a labelled chip fits. */
  variant?: 'icon' | 'pill';
}>(), {
  size: '0.7rem',
  variant: 'icon',
});

interface IMarker {
  key: string;
  icon: string;
  /** Foreground, from the design system's semantic palette. */
  fg: string;
  /** Tint behind the pill variant. */
  bg: string;
  label: string;
  title: string;
}

/**
 * The at-a-glance flags on a calendar appointment.
 *
 * Colours follow the design system's semantic set rather than being picked per marker:
 * a settled visit is Success, a deposit is Warning, and a first-timer is Primary — it is
 * a highlight, not a status. Order is by urgency, so a warning is always first
 * (rightmost, RTL) and can't be crowded out by the others.
 */
const markers = computed<IMarker[]>(() => {
  const list: IMarker[] = [];

  if (props.event.customerHasWarning) {
    list.push({
      key: 'warning',
      icon: 'pi-exclamation-triangle',
      fg: '#b91c1c',
      bg: '#fee2e2',
      label: 'אזהרה',
      title: 'ללקוח זה יש אזהרה',
    });
  }

  if (props.event.isFirstVisit) {
    list.push({
      key: 'first-visit',
      icon: 'pi-user-plus',
      fg: '#6831d0',
      bg: '#edeafd',
      label: 'חדש/ה',
      title: 'לקוח/ה חדש/ה — תור ראשון',
    });
  }

  // A 100% advance is already normalised to `success` upstream, so 'partial' here really
  // does mean a deposit was paid and a balance is still owed.
  if (props.event.paymentStatus === PaymentRequestStatus.Success) {
    list.push({
      key: 'paid',
      icon: 'pi-check-circle',
      fg: '#15803d',
      bg: '#dcfce7',
      label: 'שולם',
      title: 'שולם במלואו',
    });
  } else if (props.event.paymentStatus === 'partial') {
    list.push({
      key: 'partially-paid',
      icon: 'pi-wallet',
      fg: '#a16207',
      bg: '#fef9c3',
      label: 'מקדמה',
      title: 'שולמה מקדמה',
    });
  }

  return list;
});
</script>
